import ReactDOM from 'react-dom';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import SearchableSelect from '../ui/SearchableSelect';
import { moveTargets, moveToTarget } from '../../extensions/bundled/documents/documentsActions';
import { bumpDocumentsTree } from '../../extensions/bundled/documents/documentsUiStore';
import { useNotificationStore } from '../../store/notificationStore';
import { notifyMcpSurfaceChanged } from '../../mcp/notify';
import { relocateDocumentTabs } from '../document-editor/relocateTabs';
import { errorMessage } from '../../utils/errorMessage';

const SECONDARY = 'px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm';
const PRIMARY = 'px-3 py-1.5 rounded-lg transition-colors modern-button text-sm bg-blue-600 hover:bg-blue-700 text-white';

/**
 * Duplicate a page that has sub-pages: the page alone, or the whole subtree.
 * Portalled — `Modal` does not portal, and inside the sidebar's scroll
 * container it would paint under the app chrome (see TreeSection).
 */
export function DuplicateDialog({ node, onCancel, onDuplicate }) {
  const { t } = useTranslation();
  return ReactDOM.createPortal(
    <Modal isOpen onClose={onCancel} title={t('docSidebar.duplicate.title')}>
      <div className="space-y-3">
        <p className="text-sm text-gray-600 dark:text-zinc-300">
          {t('docSidebar.duplicate.message', { title: node.name || t('docSidebar.untitled') })}
        </p>
        <div className="flex justify-end space-x-2 pt-2">
          <button type="button" className={SECONDARY} onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button type="button" className={SECONDARY} onClick={() => onDuplicate(node, false)}>
            {t('docSidebar.duplicate.pageOnly')}
          </button>
          <button type="button" className={PRIMARY} onClick={() => onDuplicate(node, true)}>
            {t('docSidebar.duplicate.withSubpages')}
          </button>
        </div>
      </div>
    </Modal>,
    document.body
  );
}

/**
 * "Move to…" (card 3.4's picker): every folder and page of this tree the node
 * may move into (SearchableSelect + OsScroll), the top of the tree, and the
 * other personal trees — Personal and each user workspace (card 5.3) — as
 * top-level targets. A move between workspaces re-points every open tab of
 * the moved tree (relocateDocumentTabs).
 */
export function MoveToDialog({ node, nodes, scope, workspaces = [], onClose, onMoved }) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const options = useMemo(
    () =>
      moveTargets(node, nodes, { scope, scopes: workspaces, pages: true }).map((target, i) => ({
        value: String(i),
        target,
        label:
          target.kind === 'root'
            ? t('docSidebar.moveTo.top')
            : target.kind === 'workspace'
              ? t('docSidebar.workspaces.moveTarget', { name: target.name || t('docSidebar.scope.personal') })
              : target.path.map((p) => p || t('docSidebar.untitled')).join(' › '),
      })),
    [node, nodes, scope, workspaces, t]
  );
  const picked = options.find((o) => o.value === choice);

  const submit = async () => {
    if (!picked || busy) return;
    setBusy(true);
    try {
      await moveToTarget(node, picked.target, scope, nodes);
      notifyMcpSurfaceChanged('documents');
      if (picked.target.kind === 'workspace') {
        bumpDocumentsTree();
        await relocateDocumentTabs();
      }
      onMoved();
    } catch (err) {
      useNotificationStore.getState().error(errorMessage(err, t('docSidebar.errors.move')), t('docSidebar.menu.moveTo'));
      setBusy(false);
    }
  };

  return ReactDOM.createPortal(
    <Modal isOpen onClose={onClose} title={t('docSidebar.moveTo.title', { title: node.name || t('docSidebar.untitled') })}>
      <div className="space-y-3">
        <SearchableSelect
          options={options}
          value={choice}
          onChange={(key) => setChoice(key)}
          placeholder={t('docSidebar.moveTo.placeholder')}
          searchPlaceholder={t('docSidebar.moveTo.search')}
          emptyText={t('docSidebar.moveTo.empty')}
          noMatchText={t('docSidebar.moveTo.noMatch')}
        />
        <div className="flex justify-end space-x-2 pt-2">
          <button type="button" className={SECONDARY} onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className={PRIMARY} disabled={!picked || busy} onClick={submit}>
            {t('docSidebar.moveTo.submit')}
          </button>
        </div>
      </div>
    </Modal>,
    document.body
  );
}
